/**
 * The Decoupling Calculator's form state, and the pure functions around it.
 *
 * Kept out of the page component so the snapshot round-trip — what "Save to History"
 * writes and what a reopened entry restores — can be tested directly. Every field the
 * page holds must survive normalizeDecouplingState, or reopening a saved case would
 * silently drop it.
 */

import type { CitizenshipType, PropertyCount } from '../../utils/stampDutyRates';
import type { CalculatorProfile } from '../services/profileService';
import {
  DEFAULT_DECOUPLING_FEES,
  type DecouplingFees,
  type DecouplingInputs,
  type DecouplingPropertyTab,
} from './decouplingCalculator';

function toDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export const TODAY_STR = toDateStr(new Date());


/** Everything the page holds. Kept flat so the history snapshot stays readable. */
export interface DecouplingState {
  tab: DecouplingPropertyTab;
  marketValue: number;
  outstandingLoan: number;
  /** Whether Seller's Stamp Duty is applied — the toggle above the purchase date. */
  ssdApplies: boolean;
  purchaseDate: string;
  transferDate: string;

  exitingName: string;
  exitingSharePct: number;
  exitingCpf: number;
  exitingCitizenship: CitizenshipType;

  remainingName: string;
  remainingCpf: number;
  remainingCitizenship: CitizenshipType;
  remainingPropertyCount: PropertyCount;

  age: number;
  monthlyIncome: number;
  otherAnnualIncome: number;
  isSelfEmployed: boolean;
  existingMonthlyDebt: number;
  cashAvailable: number;
  cpfAvailable: number;
  tenure: number;
  /** The bank's contracted rate — what the repayment actually costs. */
  interestRate: number;
  hasOtherHousingLoan: boolean;

  /** Which loaded profile fills each side. Null until a profile is assigned; carried in
   *  the snapshot so a reopened case writes back to the same people. */
  exitingProfileId: number | null;
  remainingProfileId: number | null;

  fees: DecouplingFees;
}

// The form opens empty of client figures. What is seeded is structural rather than a
// guess: the common 50-50 split, today's date, the dropdowns' first option, a starting
// age and tenure for the sliders, the going rate for conveyancing, and a bank rate the
// agent moves to match the actual offer.
export const DEFAULT_STATE: DecouplingState = {
  tab: 'private',
  marketValue: 0,
  outstandingLoan: 0,
  ssdApplies: false,
  purchaseDate: '',
  transferDate: TODAY_STR,

  exitingName: '',
  exitingSharePct: 50,
  exitingCpf: 0,
  exitingCitizenship: 'SC',

  remainingName: '',
  remainingCpf: 0,
  remainingCitizenship: 'SC',
  remainingPropertyCount: '1st',

  age: 40,
  monthlyIncome: 0,
  otherAnnualIncome: 0,
  isSelfEmployed: false,
  existingMonthlyDebt: 0,
  cashAvailable: 0,
  cpfAvailable: 0,
  tenure: 25,
  interestRate: 2.0,
  hasOtherHousingLoan: false,

  exitingProfileId: null,
  remainingProfileId: null,

  fees: { ...DEFAULT_DECOUPLING_FEES },
};

/**
 * Back-compat for snapshots saved before a field existed. Every value is coerced to the
 * default's type, so an older or hand-edited history entry can never crash the page.
 */
export function normalizeDecouplingState(raw: unknown): DecouplingState {
  const s = (raw ?? {}) as Record<string, unknown>;
  const num = (key: keyof DecouplingState, fallback: number): number => {
    const v = s[key];
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  };
  const str = (key: keyof DecouplingState, fallback: string): string => {
    const v = s[key];
    return typeof v === 'string' ? v : fallback;
  };
  const bool = (key: keyof DecouplingState, fallback: boolean): boolean => {
    const v = s[key];
    return typeof v === 'boolean' ? v : fallback;
  };

  const rawFees = (s.fees ?? {}) as Record<string, unknown>;
  const fees = { ...DEFAULT_DECOUPLING_FEES };
  (Object.keys(DEFAULT_DECOUPLING_FEES) as (keyof DecouplingFees)[]).forEach((key) => {
    const v = rawFees[key];
    if (typeof v === 'number' && Number.isFinite(v)) fees[key] = v;
  });

  // EC and HDB were withdrawn from the page — an HDB flat cannot be decoupled at all,
  // and an EC only after MOP, at which point it is private property. Snapshots saved
  // while those tabs existed reopen as Private.
  const tab: DecouplingPropertyTab = 'private';
  const citizenship = (key: keyof DecouplingState, fallback: CitizenshipType): CitizenshipType => {
    const v = s[key];
    return v === 'SC' || v === 'SPR' || v === 'FOREIGNER' ? v : fallback;
  };
  const count = s.remainingPropertyCount;

  return {
    tab,
    marketValue: num('marketValue', DEFAULT_STATE.marketValue),
    outstandingLoan: num('outstandingLoan', DEFAULT_STATE.outstandingLoan),
    // Entries saved before the toggle existed had SSD applied whenever a purchase date
    // was set, so that is what they reopen as.
    ssdApplies: bool('ssdApplies', Boolean(str('purchaseDate', ''))),
    purchaseDate: str('purchaseDate', DEFAULT_STATE.purchaseDate),
    transferDate: str('transferDate', DEFAULT_STATE.transferDate),

    exitingName: str('exitingName', ''),
    exitingSharePct: num('exitingSharePct', DEFAULT_STATE.exitingSharePct),
    exitingCpf: num('exitingCpf', 0),
    exitingCitizenship: citizenship('exitingCitizenship', 'SC'),

    remainingName: str('remainingName', ''),
    remainingCpf: num('remainingCpf', 0),
    remainingCitizenship: citizenship('remainingCitizenship', 'SC'),
    remainingPropertyCount: count === '2nd' || count === '3rd+' ? count : '1st',

    age: num('age', DEFAULT_STATE.age),
    monthlyIncome: num('monthlyIncome', DEFAULT_STATE.monthlyIncome),
    otherAnnualIncome: num('otherAnnualIncome', 0),
    isSelfEmployed: bool('isSelfEmployed', false),
    existingMonthlyDebt: num('existingMonthlyDebt', 0),
    cashAvailable: num('cashAvailable', 0),
    cpfAvailable: num('cpfAvailable', 0),
    tenure: num('tenure', DEFAULT_STATE.tenure),
    interestRate: num('interestRate', DEFAULT_STATE.interestRate),
    hasOtherHousingLoan: bool('hasOtherHousingLoan', false),

    exitingProfileId: typeof s.exitingProfileId === 'number' ? s.exitingProfileId : null,
    remainingProfileId: typeof s.remainingProfileId === 'number' ? s.remainingProfileId : null,

    fees,
  };
}

/** Saved profiles record how many properties are held; the duty tables want a band. */
export function propertiesOwnedToPropertyCount(propertiesOwned: number | null | undefined): PropertyCount {
  if (propertiesOwned === 2) return '2nd';
  if (propertiesOwned === 3) return '3rd+';
  return '1st';
}

/** Whole-year age from a saved birth year, matching the other calculators (fallback 30). */
export function ageFromBirthYear(birthYear: number | null | undefined): number {
  return birthYear ? new Date().getFullYear() - birthYear : 30;
}

/** Every debt line a profile carries, summed into the single monthly figure TDSR uses. */
export function monthlyDebtOf(profile: CalculatorProfile): number {
  return Math.round(
    (profile.CarLoan ?? 0) + (profile.HousingLoan ?? 0)
    + (profile.CreditCard ?? 0) + (profile.OtherLoan ?? 0),
  );
}

/** A display name that never comes back blank, so the role picker always has labels. */
export function memberLabel(profile: CalculatorProfile, index: number): string {
  return profile.Name?.trim() || `Owner ${index + 1}`;
}

/** The page state, reshaped into what the maths module takes. */
export function toCalcInputs(s: DecouplingState): DecouplingInputs {
  return {
    tab: s.tab,
    property: {
      marketValue: s.marketValue,
      outstandingLoan: s.outstandingLoan,
      ssdApplies: s.ssdApplies,
      purchaseDate: s.purchaseDate,
      transferDate: s.transferDate,
    },
    exiting: {
      name: s.exitingName.trim() || undefined,
      sharePct: s.exitingSharePct,
      cpfWithdrawal: s.exitingCpf,
      citizenship: s.exitingCitizenship,
      // The exiting owner acquires nothing, so their count never drives a duty here.
      propertyCount: '1st',
    },
    remaining: {
      name: s.remainingName.trim() || undefined,
      sharePct: Math.max(0, 100 - s.exitingSharePct),
      cpfWithdrawal: s.remainingCpf,
      citizenship: s.remainingCitizenship,
      propertyCount: s.remainingPropertyCount,
    },
    fees: s.fees,
    financing: {
      age: s.age,
      monthlyIncome: s.monthlyIncome,
      otherAnnualIncome: s.otherAnnualIncome,
      isSelfEmployed: s.isSelfEmployed,
      existingMonthlyDebt: s.existingMonthlyDebt,
      cashAvailable: s.cashAvailable,
      cpfAvailable: s.cpfAvailable,
      tenure: s.tenure,
      stressTestRate: 4,
      bankRate: s.interestRate,
      hasOtherHousingLoan: s.hasOtherHousingLoan,
    },
  };
}
